import { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { useAuth } from '../../components/auth/AuthProvider';
import { useToast } from '../../components/toast/ToastContext';
import { deleteHomeCarouselImage, getAdminHomeCarouselImages, uploadHomeCarouselImage } from '../../controllers/homeCarousel';

const acceptedImageTypes = '.png,.jpg,.jpeg,.webp,.gif,.svg,image/png,image/jpeg,image/webp,image/gif,image/svg+xml';

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function HomeCarouselAdmin() {
  const { token, hasPermission } = useAuth();
  const showToast = useToast();
  const canManage = hasPermission('documentos_admin');

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);

  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef();

  useEffect(() => {
    document.title = 'FAMA - Carrusel Home (Admin)';
  }, []);

  useEffect(() => {
    if (!canManage) {
      setLoading(false);
      return;
    }
    loadImages();
  }, [canManage, token]);

  async function loadImages() {
    setLoading(true);
    try {
      const data = await getAdminHomeCarouselImages(token);
      setImages(data || []);
    } catch (e) {
      showToast({
        message: e.message || 'No se pudieron cargar las imágenes del carrusel.',
        bg: 'danger',
        autohide: true,
        delay: 7000,
        title: 'Error al cargar carrusel'
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!selectedImage) return;

    setUploading(true);
    try {
      await uploadHomeCarouselImage(token, selectedImage);
      setSelectedImage(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      showToast({
        message: 'Imagen subida correctamente.',
        bg: 'success',
        autohide: true,
        delay: 7000,
        title: 'Imagen subida'
      });
      await loadImages();
    } catch (e2) {
      showToast({
        message: e2.message || 'No se pudo subir la imagen.',
        bg: 'danger',
        autohide: true,
        delay: 7000,
        title: 'Error al subir imagen'
      });
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(fileName) {
    const confirmed = window.confirm(`¿Eliminar la imagen "${fileName}"?`);
    if (!confirmed) return;

    setDeletingFile(fileName);
    try {
      await deleteHomeCarouselImage(token, fileName);
      showToast({
        message: 'Imagen eliminada correctamente.',
        bg: 'success',
        autohide: true,
        delay: 7000,
        title: 'Imagen eliminada'
      });
      await loadImages();
    } catch (e) {
      showToast({
        message: e.message || 'No se pudo eliminar la imagen.',
        bg: 'danger',
        autohide: true,
        delay: 7000,
        title: 'Error al eliminar imagen'
      });
    } finally {
      setDeletingFile(null);
    }
  }

  if (!canManage) {
    return (
      <Container className='pt-5 pb-5'>
        <Alert variant='danger'>No tienes permiso para acceder a esta sección.</Alert>
      </Container>
    );
  }

  return (
    <Container className='pt-5 pb-5'>
      <Row className='mb-4'>
        <Col>
          <h1>Administración del Carrusel de Inicio</h1>
          <p className='text-muted mb-0'>Sube y elimina imágenes para mostrarlas en el carrusel de la página principal.</p>
        </Col>
        <Col md='auto' className='d-flex align-items-center'>
          <Badge bg='dark'>Modo Administrador</Badge>
        </Col>
      </Row>

      <Card className='mb-4'>
        <Card.Body>
          <Form onSubmit={handleUpload}>
            <Row className='align-items-end g-3'>
              <Col md={8}>
                <Form.Label>Nueva imagen</Form.Label>
                <Form.Control
                  ref={fileInputRef}
                  type='file'
                  accept={acceptedImageTypes}
                  onChange={e => setSelectedImage(e.target.files?.[0] || null)}
                  required
                />
                <Form.Text className='text-muted'>Formatos permitidos: PNG, JPG, WEBP, GIF, SVG. Máximo: 6 MB.</Form.Text>
              </Col>
              <Col md={4}>
                <Button type='submit' disabled={!selectedImage || uploading}>
                  {uploading ? <Spinner size='sm' animation='border' /> : 'Subir imagen'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {loading && <div className='text-center py-5'><Spinner animation='border' /></div>}

      {!loading && images.length === 0 && (
        <Alert variant='info'>No hay imágenes subidas. El carrusel usará la imagen por defecto.</Alert>
      )}

      <Row className='g-4'>
        {!loading && images.map((image) => (
          <Col key={image.id} md={6} lg={4}>
            <Card className='h-100'>
              <Card.Img variant='top' src={image.url} alt={image.fileName} style={{ maxHeight: '220px', objectFit: 'cover' }} />
              <Card.Body className='d-flex flex-column'>
                <Card.Title className='small text-break'>{image.fileName}</Card.Title>
                <p className='text-muted small mb-2'>Tamaño: {formatSize(image.size || 0)}</p>
                <p className='text-muted small mb-3'>Subida: {new Date(image.createdAt).toLocaleString()}</p>
                <div className='mt-auto'>
                  <Button
                    variant='outline-danger'
                    size='sm'
                    onClick={() => handleDelete(image.fileName)}
                    disabled={deletingFile === image.fileName}
                  >
                    {deletingFile === image.fileName ? <Spinner size='sm' animation='border' /> : 'Eliminar'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </Container>
  );
}

export default HomeCarouselAdmin;
